import { useEffect, useState } from 'react';
import { api, type DayLog, type Macros } from './api.js';

interface TargetRow { kcal: number; protein_g: string; carb_g: string; fat_g: string; estimated_tdee: number; deficit_kcal: number; basis: string; rationale_text: string | null }

function Ring({ label, used, target, unit }: { label: string; used: number; target: number; unit: string }): JSX.Element {
  const pct = target > 0 ? Math.min(100, (used / target) * 100) : 0;
  const over = used > target;
  return (
    <div className="metric">
      <div className="metric-head">
        <span>{label}</span>
        <span className={over ? 'bad' : ''}>{Math.round(used)}<span className="muted"> / {Math.round(target)}{unit}</span></span>
      </div>
      <div className="bar"><div className={`bar-fill${over ? ' over' : ''}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export function Today(): JSX.Element {
  const [day, setDay] = useState<DayLog | null>(null);
  const [target, setTarget] = useState<TargetRow | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api<DayLog>('/log/day'),
      api<TargetRow | null>('/targets/current'),
    ]).then(([d, t]) => { setDay(d); setTarget(t); })
      .catch((e: Error) => setErr(e.message));
  }, []);

  if (err) return <p className="bad">{err}</p>;
  if (!day) return <p className="muted">Loading…</p>;

  const t: Macros | null = day.target;

  return (
    <>
      {!t && (
        <div className="card warn">
          <h2>No targets yet</h2>
          <p className="muted">Finish the setup guide so the engine can compute your calories and macros.</p>
        </div>
      )}

      {t && (
        <div className="card">
          <h2>Today</h2>
          <Ring label="Calories" used={day.totals.kcal} target={t.kcal} unit="" />
          <Ring label="Protein" used={day.totals.proteinG} target={t.proteinG} unit="g" />
          <Ring label="Carbs" used={day.totals.carbG} target={t.carbG} unit="g" />
          <Ring label="Fat" used={day.totals.fatG} target={t.fatG} unit="g" />
          {day.remaining && (
            <p className="muted small">
              Remaining: {Math.round(day.remaining.kcal)} kcal, {day.remaining.proteinG}g protein
            </p>
          )}
        </div>
      )}

      {target && (
        <div className="card">
          <h2>How these were set</h2>
          <ul className="kv">
            <li><span>Estimated TDEE</span><span>{target.estimated_tdee} kcal</span></li>
            <li><span>Deficit</span><span>{target.deficit_kcal} kcal</span></li>
            <li><span>Basis</span><span>{target.basis.replace(/_/g, ' ')}</span></li>
          </ul>
          {target.rationale_text && (
            <p className="muted small">Guardrails applied: {target.rationale_text}</p>
          )}
        </div>
      )}

      <div className="card">
        <h2>Logged today</h2>
        {day.entries.length === 0 && <p className="muted">Nothing logged yet.</p>}
        {day.entries.length > 0 && (
          <table className="tbl">
            <thead><tr><th>Food</th><th>g</th><th>kcal</th><th>P</th></tr></thead>
            <tbody>
              {day.entries.map((e, i) => (
                <tr key={String(e['id'] ?? i)}>
                  <td>{String(e['name'])}{e['brand'] ? <span className="muted"> {String(e['brand'])}</span> : null}</td>
                  <td>{String(e['grams'])}</td>
                  <td>{String(e['kcal'])}</td>
                  <td>{String(e['protein_g'])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
